import React, { useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { authApi } from '@/services/api';
import { LoadingSpinner } from '@/components/Layout';

export function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (!token) {
      setError('Invalid reset link. Please request a new one.');
      return;
    }

    setIsSubmitting(true);

    try {
      await authApi.resetPassword(token, password);
      setSuccess(true);
      setTimeout(() => navigate('/login'), 3000);
    } catch (err: any) {
      if (err.response?.status === 429) {
        setError('Too many attempts. Please wait a few minutes before trying again.');
      } else {
        setError(err.response?.data?.message || 'Failed to reset password. The link may have expired.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <img
              src="https://kederscreens.com/wp-content/uploads/2025/07/Keder-logos-1-scaled.png"
              alt="Keder Screens"
              className="h-16 mx-auto mb-4"
            />
          </div>
          <div className="card text-center">
            <div className="bg-red-900/50 border border-red-500 text-red-200 px-4 py-4 rounded-lg mb-6">
              <p className="font-medium">Invalid Reset Link</p>
              <p className="text-sm mt-1">This password reset link is invalid or has expired.</p>
            </div>
            <Link to="/forgot-password" className="text-keder-yellow hover:underline">
              Request a new reset link
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <img
            src="https://kederscreens.com/wp-content/uploads/2025/07/Keder-logos-1-scaled.png"
            alt="Keder Screens"
            className="h-16 mx-auto mb-4"
          />
          <p className="text-gray-400 mt-2">Dealer Portal</p>
        </div>

        {/* Card */}
        <div className="card">
          <h2 className="text-xl font-semibold text-white mb-2">Set new password</h2>
          <p className="text-gray-400 text-sm mb-6">
            Enter your new password below.
          </p>

          {success ? (
            <div className="text-center">
              <div className="bg-green-900/50 border border-green-500 text-green-200 px-4 py-4 rounded-lg mb-6">
                <p className="font-medium">Password reset successfully!</p>
                <p className="text-sm mt-1">Redirecting you to sign in...</p>
              </div>
              <Link to="/login" className="text-keder-yellow hover:underline">
                Sign in now
              </Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <div className="bg-red-900/50 border border-red-500 text-red-200 px-4 py-3 rounded-lg text-sm">
                  {error}
                </div>
              )}

              <div>
                <label htmlFor="password" className="label">
                  New Password
                </label>
                <input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="input-field"
                  placeholder="••••••••"
                  required
                  minLength={8}
                />
                <p className="text-xs text-gray-500 mt-1">Minimum 8 characters</p>
              </div>

              <div>
                <label htmlFor="confirmPassword" className="label">
                  Confirm New Password
                </label>
                <input
                  id="confirmPassword"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="input-field"
                  placeholder="••••••••"
                  required
                />
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="btn-primary w-full flex items-center justify-center gap-2"
              >
                {isSubmitting ? (
                  <>
                    <LoadingSpinner size="sm" />
                    Resetting...
                  </>
                ) : (
                  'Reset password'
                )}
              </button>

              <div className="text-center">
                <Link to="/login" className="text-sm text-gray-400 hover:text-keder-yellow">
                  Back to sign in
                </Link>
              </div>
            </form>
          )}
        </div>

        {/* Footer */}
        <p className="text-center text-gray-500 text-sm mt-8">
          © {new Date().getFullYear()} Keder Screens. All rights reserved.
        </p>
      </div>
    </div>
  );
}

export default ResetPasswordPage;
